import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Target, PieChart as PieIcon, MapPin, AlertTriangle, Lock, Mail, FileDown } from 'lucide-react';
import { API } from '../config/api';

function Section({ title, icon: Icon, children, accent = '#C99A55' }) {
  return (
    <div>
      <h3 className="font-heading text-lg text-[#493329] mb-4 flex items-center gap-2 mt-2">
        <span className="w-6 h-0.5" style={{ background: accent }} />
        <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.8} />
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function CoachInsightsPanel() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/export-banker-pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `PURO_Rapport_Prelancement_${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Impossible de générer le rapport. Réessayez dans un instant.');
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [seg, goal, plans, sources, suspicious, priv] = await Promise.all([
        axios.get(`${API}/admin/insights/segmentation`, { withCredentials: true }),
        axios.get(`${API}/admin/insights/goal`, { withCredentials: true }),
        axios.get(`${API}/admin/insights/plan-breakdown`, { withCredentials: true }),
        axios.get(`${API}/admin/insights/sources`, { withCredentials: true }),
        axios.get(`${API}/admin/suspicious`, { withCredentials: true }),
        axios.get(`${API}/admin/private-classes`, { withCredentials: true }),
      ]);
      setData({
        seg: seg.data, goal: goal.data, plans: plans.data,
        sources: sources.data, suspicious: suspicious.data,
        privateClasses: priv.data,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data.seg) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  const { seg, goal, plans, sources, suspicious, privateClasses } = data;

  return (
    <div className="space-y-6" data-testid="coach-insights-panel">
      {/* Banker PDF Export */}
      <Card className="border-[#C99A55]/30 rounded-2xl bg-gradient-to-br from-[#FBF7EE] to-[#F5EFE5]" data-testid="banker-export-block">
        <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C99A55] font-semibold mb-1">Rapport de prélancement</p>
            <p className="text-sm text-[#493329] leading-relaxed">
              Générez un PDF professionnel <strong>prêt à présenter à votre banquier</strong> — statistiques, projections et modèle économique inclus.
            </p>
          </div>
          <Button
            onClick={handleExportPdf}
            disabled={exporting}
            className="bg-[#493329] hover:bg-[#C99A55] text-white rounded-full px-6 h-11 text-sm transition-all duration-300 whitespace-nowrap"
            data-testid="banker-pdf-export-btn"
          >
            {exporting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> Génération…</>
            ) : (
              <><FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} /> Exporter en PDF</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Segmentation */}
      <Card className="border-[#E9DDCC] rounded-2xl" data-testid="segmentation-block">
        <CardContent className="p-6">
          <Section title="Segmentation des données" icon={Lock}>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#F5EFE5] rounded-xl p-4 border border-[#E9DDCC] text-center">
                <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Contacts</div>
                <div className="font-heading text-2xl text-[#493329]">{seg.contacts}</div>
              </div>
              <div className="bg-[#FFF8E6] rounded-xl p-4 border border-[#C99A55]/30 text-center">
                <div className="text-[10px] uppercase text-[#C99A55] tracking-wider mb-1">Préinscrites</div>
                <div className="text-[10px] text-[#AE9885] mb-1">non confirmées</div>
                <div className="font-heading text-2xl text-[#C99A55]">{seg.pre_unconfirmed}</div>
              </div>
              <div className="bg-[#F1F6F0] rounded-xl p-4 border border-[#7C9C82]/30 text-center">
                <div className="text-[10px] uppercase text-[#7C9C82] tracking-wider mb-1">Confirmées</div>
                <div className="font-heading text-2xl text-[#7C9C82]">{seg.confirmed}</div>
              </div>
            </div>
            <p className="text-[11px] text-[#AE9885] italic mt-3">Les statistiques ci-dessous sont basées sur les clientes confirmées.</p>
          </Section>
        </CardContent>
      </Card>

      {/* Goal 70 */}
      <Card className="border-[#E9DDCC] rounded-2xl bg-gradient-to-br from-[#F3EADA] to-[#F5EFE5]" data-testid="goal-block">
        <CardContent className="p-6">
          <Section title="Objectif de remplissage" icon={Target}>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="font-heading text-3xl text-[#493329]">{goal.current}</span>
              <span className="text-[#AE9885]">/ {goal.goal}</span>
              <span className="text-[#C99A55] font-semibold">— {goal.percent}% atteint</span>
              <span className="text-[#7A6455] text-sm ml-auto">{goal.remaining} restantes</span>
            </div>
            <div className="h-3 bg-[#EFE4D0] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#C99A55] to-[#B08A4F] rounded-full transition-all duration-500" style={{ width: `${goal.percent}%` }} />
            </div>
          </Section>
        </CardContent>
      </Card>

      {/* Plan breakdown + Revenue */}
      <Card className="border-[#E9DDCC] rounded-2xl" data-testid="plan-breakdown-block">
        <CardContent className="p-6">
          <Section title="Répartition des formules" icon={PieIcon}>
            <div className="space-y-2.5">
              {plans.items.map((p, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[#493329] font-medium">{p.name}</span>
                    <span className="text-[#7A6455]">{p.count} <span className="text-[#AE9885]">· {p.percent}%</span></span>
                  </div>
                  <div className="h-2 bg-[#EFE4D0] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C99A55] rounded-full" style={{ width: `${p.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 bg-[#F3EADA] border border-[#C99A55]/30 rounded-xl p-4 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-[#AE9885]">CA mensuel estimé</span>
              <span className="font-heading text-2xl text-[#C99A55] font-semibold" data-testid="estimated-revenue">{plans.estimated_monthly_revenue}€</span>
            </div>
          </Section>
        </CardContent>
      </Card>

      {/* Sources */}
      <Card className="border-[#E9DDCC] rounded-2xl" data-testid="sources-block">
        <CardContent className="p-6">
          <Section title="Provenance des clientes" icon={MapPin}>
            {sources.all.length === 0 ? (
              <p className="text-sm text-[#AE9885] italic">Aucune donnée pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {sources.all.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-[#F5EFE5] px-4 py-2 rounded-lg">
                    <span className="text-[#493329]">{s.name}</span>
                    <span className="text-[#C99A55] font-semibold">{s.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </CardContent>
      </Card>

      {/* Private classes */}
      <Card className="border-[#E9DDCC] rounded-2xl" data-testid="private-classes-block">
        <CardContent className="p-6">
          <Section title="Cours privés" icon={Mail}>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {['Nouveau', 'Contactée', 'Confirmée'].map((s, i) => (
                <div key={i} className="bg-[#F5EFE5] rounded-xl p-3 border border-[#E9DDCC] text-center">
                  <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">{s}</div>
                  <div className="font-heading text-xl text-[#493329]">{privateClasses?.by_status?.[s] || 0}</div>
                </div>
              ))}
            </div>
            {privateClasses?.items?.length === 0 ? (
              <p className="text-sm text-[#AE9885] italic">Aucune demande de cours privé pour le moment.</p>
            ) : (
              <div className="bg-white rounded-xl border border-[#E9DDCC] max-h-[260px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5EFE5] sticky top-0">
                    <tr className="text-[10px] uppercase tracking-wider text-[#AE9885]">
                      <th className="px-3 py-2 text-left font-semibold">Cliente</th>
                      <th className="px-2 py-2 text-left font-semibold">Mode</th>
                      <th className="px-2 py-2 text-left font-semibold">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EFE4D0]">
                    {privateClasses.items.slice(0, 50).map((p, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">
                          <div className="text-[#493329] font-medium truncate max-w-[180px]">{p.name}</div>
                          <div className="text-[10px] text-[#7A6455] truncate">{p.email}</div>
                        </td>
                        <td className="px-2 py-2 text-[#7A6455] capitalize">{p.mode || 'solo'}</td>
                        <td className="px-2 py-2">
                          <span className="text-[10px] font-medium inline-block px-2 py-0.5 rounded-full"
                                style={{
                                  background: p.status === 'Confirmée' ? '#E8F0E5' : p.status === 'Contactée' ? '#FFF8E6' : '#F5F2EA',
                                  color: p.status === 'Confirmée' ? '#7C9C82' : p.status === 'Contactée' ? '#C99A55' : '#7A6455',
                                }}>
                            {p.status || 'Nouveau'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </CardContent>
      </Card>

      {/* Suspicious accounts */}
      <Card className="border border-[#C76542]/30 rounded-2xl bg-[#FFF4F0]" data-testid="suspicious-block">
        <CardContent className="p-6">
          <Section title="Comptes à vérifier" icon={AlertTriangle} accent="#C76542">
            {suspicious.count === 0 ? (
              <p className="text-sm text-[#7A6455] italic">Aucun compte suspect détecté.</p>
            ) : (
              <div className="bg-white rounded-xl border border-[#E9DDCC] max-h-[260px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5EFE5] sticky top-0">
                    <tr className="text-[10px] uppercase tracking-wider text-[#AE9885]">
                      <th className="px-3 py-2 text-left font-semibold">Compte</th>
                      <th className="px-2 py-2 text-center font-semibold">Score</th>
                      <th className="px-2 py-2 text-left font-semibold">Anomalies</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EFE4D0]">
                    {suspicious.items.map((u, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">
                          <div className="text-[#493329] font-medium truncate max-w-[180px]">{u.name || '—'}</div>
                          <div className="text-[10px] text-[#7A6455] truncate">{u.email}</div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="text-[11px] font-semibold inline-block px-2 py-0.5 rounded-full"
                                style={{
                                  background: u.badge === 'Suspect' ? '#FCE7E7' : '#FFF4DB',
                                  color: u.badge === 'Suspect' ? '#C76542' : '#C99A55',
                                }}>
                            {u.score}/100
                          </span>
                        </td>
                        <td className="px-2 py-2 text-[10px] text-[#7A6455]">
                          {(u.flags || []).join(' · ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[10px] text-[#AE9885] italic mt-2">Aucune action automatique. Liste informationnelle uniquement.</p>
          </Section>
        </CardContent>
      </Card>
    </div>
  );
}
